/**
 * String ids from level JSON map here. Unknown ids fall back + warn in dev —
 * never crash at runtime.
 */

type RegistryEntry<T> = { id: string; value: T };

function createRegistry<T>(kind: string, defaults: RegistryEntry<T>[], fallback: T) {
  const map = new Map(defaults.map((entry) => [entry.id, entry.value]));

  return {
    get(id: string): T {
      const found = map.get(id);
      if (found !== undefined) return found;
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(`[enklespill] Unknown ${kind} id "${id}" — using default`);
      }
      return fallback;
    },
    has(id: string): boolean {
      return map.has(id);
    },
    ids(): string[] {
      return Array.from(map.keys());
    },
    register(id: string, value: T) {
      map.set(id, value);
    },
  };
}

export type EnvironmentDef = {
  label: string;
  sky: string;
  ground: string;
};

export type RoutePresetDef = {
  label: string;
  /** Control points [x, z] along a simple polyline; Phase 1+ may replace with CatmullRom. */
  points: [number, number][];
};

export type CelebrationDef = {
  label: string;
  emoji: string;
};

export type ReactionDef = {
  label: string;
  emoji: string;
};

const defaultEnvironment: EnvironmentDef = {
  label: 'Default town',
  sky: '#8ecae6',
  // Yellow-green grass so no bin colour (esp. green) blends in
  ground: '#C5E86A',
};

export const environments = createRegistry<EnvironmentDef>(
  'environment',
  [
    { id: 'town-small', value: { label: 'Liten by', sky: '#9FD9F5', ground: '#C8E86E' } },
    { id: 'villa-street', value: { label: 'Villavei', sky: '#B3E0FF', ground: '#C5E86A' } },
    { id: 'farm', value: { label: 'Gård', sky: '#C5EBFF', ground: '#D0EC78' } },
    { id: 'harbor', value: { label: 'Havn', sky: '#8ED4F0', ground: '#E8D5B7' } },
    { id: 'apartments', value: { label: 'Blokker', sky: '#A8D4F0', ground: '#C8E86E' } },
    { id: 'school', value: { label: 'Skole', sky: '#B5E0FF', ground: '#C8E86E' } },
    { id: 'park', value: { label: 'Park', sky: '#A8E0F0', ground: '#BFE85A' } },
    { id: 'recycling-center', value: { label: 'Gjenbruk', sky: '#B8D4E8', ground: '#B8C4B0' } },
    { id: 'beach', value: { label: 'Strand', sky: '#87CEEB', ground: '#F6E2B3' } },
    { id: 'market-square', value: { label: 'Torg', sky: '#A8D8EA', ground: '#D4C4A8' } },
    { id: 'town-center', value: { label: 'Sentrum', sky: '#9FD9F5', ground: '#8FD36A' } },
    { id: 'yard-small', value: { label: 'Liten gårdsplass', sky: '#D6EAF8', ground: '#E8F1F8' } },
    { id: 'driveway', value: { label: 'Oppkjørsel', sky: '#D6EAF8', ground: '#E8F1F8' } },
    { id: 'kindergarten', value: { label: 'Barnehage', sky: '#D6EAF8', ground: '#E8F1F8' } },
    { id: 'parking-lot', value: { label: 'Parkering', sky: '#D6EAF8', ground: '#D0D5DD' } },
    { id: 'village-road', value: { label: 'Bygdvei', sky: '#D6EAF8', ground: '#E8F1F8' } },
    { id: 'school-road', value: { label: 'Skolevei', sky: '#D6EAF8', ground: '#E8F1F8' } },
    { id: 'yard-medium', value: { label: 'Gårdsplass', sky: '#1B2838', ground: '#E8F1F8' } },
    { id: 'airport-runway', value: { label: 'Flyplass', sky: '#D6EAF8', ground: '#C5CCD6' } },
    { id: 'mountain-pass', value: { label: 'Fjellovergang', sky: '#B8C9D9', ground: '#E8F1F8' } },
    { id: 'christmas-village', value: { label: 'Julelandsby', sky: '#2C3E50', ground: '#E8F1F8' } },
    { id: 'frozen-lake', value: { label: 'Isinnsjø', sky: '#AED6F1', ground: '#D6EAF8' } },
    { id: 'village-full', value: { label: 'Hele bygda', sky: '#D6EAF8', ground: '#E8F1F8' } },
    { id: 'campsite', value: { label: 'Camping', sky: '#8ecae6', ground: '#7BC67E' } },
    { id: 'forest', value: { label: 'Skog', sky: '#7BA3A8', ground: '#3D6B4F' } },
    { id: 'country-road', value: { label: 'Landevei', sky: '#A8C5A0', ground: '#8FAE7E' } },
    { id: 'fire-station', value: { label: 'Brannstasjon', sky: '#8ecae6', ground: '#A0A0A0' } },
  ],
  defaultEnvironment
);

const defaultRoute: RoutePresetDef = {
  label: 'Default street',
  points: [
    [-20, 0],
    [-10, 0],
    [0, 0],
    [10, 0],
    [20, 0],
  ],
};

export const routePresets = createRegistry<RoutePresetDef>(
  'route-preset',
  [
    {
      id: 'street-short',
      value: { label: 'Kort gate', points: [[-12, 0], [-4, 0], [4, 0], [12, 0]] },
    },
    {
      id: 'street-medium',
      value: { label: 'Middels gate', points: [[-18, 0], [-6, 1], [6, -1], [18, 0]] },
    },
    {
      id: 'street-long',
      value: {
        label: 'Lang gate',
        points: [[-24, 0], [-12, 1], [0, 0], [12, -1], [24, 0]],
      },
    },
    {
      id: 'farm-road',
      value: { label: 'Gårdsvei', points: [[-16, -2], [-5, 0], [5, 1], [16, 0]] },
    },
    {
      id: 'harbor-road',
      value: { label: 'Havnevei', points: [[-16, 2], [-5, 1], [5, 0], [16, -1]] },
    },
    {
      id: 'school-loop',
      value: {
        label: 'Skoleløkke',
        points: [[-14, 0], [-4, 4], [4, 4], [14, 0], [4, -4], [-4, -4], [-14, 0]],
      },
    },
    {
      id: 'park-path',
      value: { label: 'Parksti', points: [[-12, -2], [-4, 2], [4, -2], [12, 2]] },
    },
    {
      id: 'recycling-yard',
      value: { label: 'Gjenbruksgård', points: [[-8, 0], [0, 0], [8, 0]] },
    },
    {
      id: 'beach-road',
      value: { label: 'Strandvei', points: [[-14, 1], [-5, 0], [5, 0], [14, -1]] },
    },
    {
      id: 'square-loop',
      value: {
        label: 'Torgløkke',
        points: [[-10, -6], [10, -6], [10, 6], [-10, 6], [-10, -6]],
      },
    },
    {
      id: 'parade-route',
      value: {
        label: 'Paraderute',
        points: [[-20, 0], [-10, 0], [0, 0], [10, 0], [20, 0]],
      },
    },
    {
      id: 'station-to-campsite',
      value: { label: 'Stasjon→camping', points: [[-16, 0], [-6, 2], [6, 2], [16, 0]] },
    },
    {
      id: 'station-to-town',
      value: { label: 'Stasjon→by', points: [[-18, 0], [-6, 0], [6, 0], [18, 0]] },
    },
    {
      id: 'station-to-villa',
      value: { label: 'Stasjon→villa', points: [[-18, 0], [-6, 1], [6, -1], [18, 0]] },
    },
    {
      id: 'station-to-farm',
      value: { label: 'Stasjon→gård', points: [[-20, 0], [-8, 2], [4, 1], [16, 0]] },
    },
    {
      id: 'station-to-park',
      value: { label: 'Stasjon→park', points: [[-14, 0], [-4, 2], [6, 1], [14, 0]] },
    },
    {
      id: 'station-to-apartments',
      value: { label: 'Stasjon→blokk', points: [[-16, 0], [-4, 0], [6, 0], [16, 0]] },
    },
    {
      id: 'station-to-forest',
      value: { label: 'Stasjon→skog', points: [[-18, 0], [-6, 3], [4, 2], [16, 0]] },
    },
    {
      id: 'station-to-country',
      value: { label: 'Stasjon→landevei', points: [[-16, 0], [-4, 1], [6, -1], [16, 0]] },
    },
    {
      id: 'station-to-harbor',
      value: { label: 'Stasjon→havn', points: [[-16, 0], [-4, -1], [6, -2], [16, -1]] },
    },
    {
      id: 'return-to-station',
      value: { label: 'Tilbake til stasjon', points: [[16, 0], [6, 0], [-6, 0], [-16, 0]] },
    },
    {
      id: 'station-yard',
      value: { label: 'Stasjonsgård', points: [[-6, 0], [0, 0], [6, 0]] },
    },
  ],
  defaultRoute
);

export const celebrations = createRegistry<CelebrationDef>(
  'celebration',
  [
    { id: 'flowers-bloom', value: { label: 'Blomster', emoji: '🌸' } },
    { id: 'birds-fly-in', value: { label: 'Fugler', emoji: '🐦' } },
    { id: 'cow-moo', value: { label: 'Ku', emoji: '🐄' } },
    { id: 'seagulls', value: { label: 'Måker', emoji: '🕊️' } },
    { id: 'lights-on', value: { label: 'Lys', emoji: '💡' } },
    { id: 'balconies-wave', value: { label: 'Balkonger', emoji: '👋' } },
    { id: 'kids-cheer', value: { label: 'Barn jubler', emoji: '🎉' } },
    { id: 'kitten-dance', value: { label: 'Kattunge', emoji: '🐱' } },
    { id: 'confetti', value: { label: 'Konfetti', emoji: '🎊' } },
    { id: 'crabs-dance', value: { label: 'Krabber', emoji: '🦀' } },
    { id: 'rainbow', value: { label: 'Regnbue', emoji: '🌈' } },
    { id: 'market-cheer', value: { label: 'Torg', emoji: '🧺' } },
    { id: 'snowflakes', value: { label: 'Snøfnugg', emoji: '❄️' } },
    { id: 'parade', value: { label: 'Parade', emoji: '🇳🇴' } },
    { id: 'snowman-build', value: { label: 'Snømann', emoji: '⛄' } },
    { id: 'snowman-giant', value: { label: 'Kjempesnømann', emoji: '⛄' } },
    { id: 'animals-dance', value: { label: 'Dyr danser', emoji: '🐷' } },
    { id: 'bubbles', value: { label: 'Bobler', emoji: '🫧' } },
  ],
  { label: 'Feiring', emoji: '⭐' }
);

export const reactions = createRegistry<ReactionDef>(
  'reaction',
  [
    { id: 'sparkle', value: { label: 'Glitter', emoji: '✨' } },
    { id: 'honk', value: { label: 'Tut', emoji: '🚗' } },
    { id: 'bounce', value: { label: 'Sprett', emoji: '⚽' } },
    { id: 'meow', value: { label: 'Mjau', emoji: '🐱' } },
    { id: 'light-up', value: { label: 'Lys', emoji: '🏮' } },
    { id: 'birds', value: { label: 'Fugler', emoji: '🐦' } },
    { id: 'wave', value: { label: 'Vink', emoji: '👋' } },
  ],
  { label: 'Reaksjon', emoji: '✨' }
);

export const stickers = createRegistry<{ label: string }>(
  'sticker',
  [],
  { label: 'Klistremerke' }
);

export const unlocks = createRegistry<{ label: string }>(
  'unlock',
  [],
  { label: 'Opplåst' }
);
